// useChatStream — subscribes to a chat conversation's live message stream
// (routes/chat/stream.ts) via EventSource. Mirrors useSSE.ts's reconnect
// contract (Last-Event-ID replay), keyed by a message's `seq` instead of a
// RunEvent's `id`.

import { useEffect, useRef, useState } from 'react'
import type { ChatMessage } from '@goblic/shared'
import { mergeChatMessages } from '../lib/chat-messages'
import { chatApi } from '../lib/api-client'
import { probeSessionAfterStreamError } from '../lib/api-client'

export type ChatStreamStatus = 'idle' | 'connecting' | 'open' | 'error' | 'closed'

export interface UseChatStreamResult {
  messages: ChatMessage[]
  status: ChatStreamStatus
  /** Clears the locally buffered messages (e.g. when switching conversations). */
  clear: () => void
}

/**
 * Connects to `GET /chat/conversations/:id/stream` and appends every
 * received ChatMessage to `messages` (deduped/ordered by `seq`, since an
 * update — a confirmation decision — replaces its earlier entry in place
 * rather than appending a duplicate). Pass `undefined` to stay idle.
 */
export function useChatStream(conversationId: string | undefined): UseChatStreamResult {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [status, setStatus] = useState<ChatStreamStatus>('idle')
  const lastEventIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!conversationId) {
      setStatus('idle')
      return
    }

    setMessages([])
    lastEventIdRef.current = null
    setStatus('connecting')
    const source = new EventSource(
      chatApi.streamUrl(conversationId, lastEventIdRef.current ?? undefined)
    )

    source.onopen = () => setStatus('open')

    source.onmessage = (event: MessageEvent<string>) => {
      try {
        const parsed = JSON.parse(event.data) as ChatMessage
        if (event.lastEventId) {
          if (!lastEventIdRef.current || BigInt(event.lastEventId) > BigInt(lastEventIdRef.current)) lastEventIdRef.current = event.lastEventId
        }
        setMessages((previous) => mergeChatMessages(previous, [parsed]))
      } catch {
        // TODO: surface malformed-event telemetry instead of silently dropping it.
      }
    }

    source.onerror = () => {
      setStatus('error')
      probeSessionAfterStreamError()
    }

    return () => {
      source.close()
    }
  }, [conversationId])

  function clear(): void {
    setMessages([])
    lastEventIdRef.current = null
  }

  return { messages, status, clear }
}
